import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Users,
  ChevronLeft,
  ChevronRight,
  X,
  Loader2,
  AlertCircle,
  Activity,
  CheckCircle,
  Trophy,
  StickyNote,
  MessageSquare,
  Copy,
  Check,
  Zap,
  TrendingUp,
  Sparkles,
  BookOpen,
  Hand,
  ArrowRight,
  Flame
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { toast } from 'sonner';
import { LiveSessionManager, calculateSessionStats } from '../../utils/supabase/session-manager';
import { getSession, getSlideSubmissions, updateSession, getSessionParticipants, serverGetSlideSubmissions } from '../../utils/supabase/queries';
import { getCurrentUser, type Session, type Slide } from '../../utils/supabase/client';
import { syncSessionAnalytics } from '../../utils/supabase/analytics';
import { getResponseCount, TeacherSlideView } from '../components/slides';
import { getSlideConfig, isContentSlideType } from '../../utils/slides/slideRegistry';

interface Participant {
  id: string;
  student_name: string;
  joined_at: string;
  progress_percentage: number;
}

interface ActivityFeedItem {
  id: string;
  type: 'join' | 'submit';
  studentName: string;
  timestamp: Date;
  slideLabel?: string;
}

export default function LiveSessionTeacher() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  // Session state
  const [session, setSession] = useState<Session | null>(null);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Stats
  const [stats, setStats] = useState({
    totalParticipants: 0,
    totalSubmissions: 0,
    averageProgress: 0,
    completionRate: 0
  });

  // UI state
  const [codeCopied, setCodeCopied] = useState(false);
  const [activityFeed, setActivityFeed] = useState<ActivityFeedItem[]>([]);
  const prevParticipantCount = useRef(0);
  const prevSubmissionCount = useRef(0);

  // Real-time manager
  const [sessionManager, setSessionManager] = useState<LiveSessionManager | null>(null);

  // Submissions for current slide
  const [currentSlideSubmissions, setCurrentSlideSubmissions] = useState<any[]>([]);

  // Load session on mount
  useEffect(() => {
    if (sessionId) {
      initializeSession();
    }

    return () => {
      if (sessionManager) {
        sessionManager.cleanup();
      }
    };
  }, [sessionId]);

  // Load submissions when slide changes
  useEffect(() => {
    if (slides[currentSlide]) {
      loadSlideSubmissions();
    }
  }, [currentSlide, slides]);

  // Refresh stats periodically
  useEffect(() => {
    if (sessionId) {
      const interval = setInterval(refreshStats, 5000);
      return () => clearInterval(interval);
    }
  }, [sessionId]);

  // Refresh participants list periodically to catch new joins and score updates
  useEffect(() => {
    if (!sessionId) return;

    const pollParticipants = async () => {
      try {
        const updatedParticipants = await getSessionParticipants(sessionId);
        
        // Track new joins for activity feed
        if (updatedParticipants.length > prevParticipantCount.current) {
          const newStudents = updatedParticipants.slice(prevParticipantCount.current);
          const newFeedItems: ActivityFeedItem[] = newStudents.map(p => ({
            id: `join-${p.id}-${Date.now()}`,
            type: 'join' as const,
            studentName: p.student_name,
            timestamp: new Date()
          }));
          setActivityFeed(prev => [...newFeedItems, ...prev].slice(0, 20));
        }
        prevParticipantCount.current = updatedParticipants.length;
        
        setParticipants(updatedParticipants);
      } catch (error) {
        console.error('Error polling participants:', error);
      }
    };

    const interval = setInterval(pollParticipants, 2000);
    return () => clearInterval(interval);
  }, [sessionId]);

  // Refresh slide submissions periodically for Collaborate Board and other activities
  useEffect(() => {
    if (!sessionId || !slides.length) return;

    const pollSubmissions = async () => {
      try {
        if (slides && slides[currentSlide]) {
          const currentSlideData = slides[currentSlide];
          const submissions = currentSlideData.type === 'collaborate_board'
            ? await serverGetSlideSubmissions(sessionId, currentSlideData.id)
            : await getSlideSubmissions(sessionId, currentSlideData.id);
          
          // Track new submissions for activity feed
          if (submissions.length > prevSubmissionCount.current) {
            const newSubs = submissions.slice(prevSubmissionCount.current);
            const slideConfig = getSlideConfig(currentSlideData.type);
            const newFeedItems: ActivityFeedItem[] = newSubs.map((s: any) => ({
              id: `sub-${s.id}-${Date.now()}`,
              type: 'submit' as const,
              studentName: s.student_name || 'A student',
              timestamp: new Date(),
              slideLabel: slideConfig?.label || currentSlideData.type
            }));
            setActivityFeed(prev => [...newFeedItems, ...prev].slice(0, 20));
          }
          prevSubmissionCount.current = submissions.length;
          
          setCurrentSlideSubmissions(submissions);
        }
      } catch (error) {
        console.error('Error polling submissions:', error);
      }
    };

    prevSubmissionCount.current = 0;
    pollSubmissions();
    const interval = setInterval(pollSubmissions, 2000);
    return () => clearInterval(interval);
  }, [sessionId, currentSlide, slides]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, sessionManager, slides.length]);

  const initializeSession = async () => {
    if (!sessionId) return;

    setLoading(true);
    try {
      const user = await getCurrentUser();
      if (!user) {
        toast.error('Please sign in to continue');
        navigate('/teacher/login');
        return;
      }

      const manager = new LiveSessionManager(sessionId, {
        onSlideChange: (slideNumber) => {
          setCurrentSlide(slideNumber);
        },
        onNewParticipant: (participant) => {
          setParticipants(prev => [...prev, participant as any]);
          refreshStats();
        },
        onNewSubmission: (submission) => {
          loadSlideSubmissions();
          refreshStats();
        }
      });

      const sessionState = await manager.initialize();

      if (sessionState.session.teacher_id !== user.id) {
        toast.error('You do not have permission to access this session');
        navigate('/teacher/dashboard');
        return;
      }

      setSession(sessionState.session);
      setSlides(sessionState.slides);
      setCurrentSlide(sessionState.currentSlide);
      setParticipants(sessionState.participants);
      setSessionManager(manager);
      prevParticipantCount.current = sessionState.participants.length;

      await refreshStats();
    } catch (error) {
      console.error('Error initializing session:', error);
      toast.error('Failed to load session');
      navigate('/teacher/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const refreshStats = async () => {
    if (!sessionId) return;
    try {
      const newStats = await calculateSessionStats(sessionId);
      setStats(newStats);
    } catch (error) {
      console.error('Error refreshing stats:', error);
    }
  };

  const loadSlideSubmissions = async () => {
    if (!sessionId || !slides[currentSlide]) return;
    try {
      const currentSlideData = slides[currentSlide];
      const submissions = currentSlideData.type === 'collaborate_board'
        ? await serverGetSlideSubmissions(sessionId, currentSlideData.id)
        : await getSlideSubmissions(sessionId, currentSlideData.id);
      setCurrentSlideSubmissions(submissions);
    } catch (error) {
      console.error('Error loading submissions:', error);
    }
  };

  const handlePrevious = async () => {
    if (!sessionManager || currentSlide === 0) return;
    try {
      const newSlide = currentSlide - 1;
      setCurrentSlide(newSlide);
      await sessionManager.previousSlide(currentSlide);
    } catch (error) {
      console.error('Error navigating to previous slide:', error);
      toast.error('Failed to navigate');
      if (sessionId) {
        const session = await getSession(sessionId);
        setCurrentSlide(session.current_slide);
      }
    }
  };

  const handleNext = async () => {
    if (!sessionManager || currentSlide >= slides.length - 1) return;
    try {
      const newSlide = currentSlide + 1;
      setCurrentSlide(newSlide);
      await sessionManager.nextSlide(currentSlide, slides.length);
    } catch (error) {
      console.error('Error navigating to next slide:', error);
      toast.error('Failed to navigate');
      if (sessionId) {
        const session = await getSession(sessionId);
        setCurrentSlide(session.current_slide);
      }
    }
  };

  const handleGoToSlide = async (slideNumber: number) => {
    if (!sessionManager) return;
    try {
      setCurrentSlide(slideNumber);
      await sessionManager.goToSlide(slideNumber);
    } catch (error) {
      console.error('Error navigating to slide:', error);
      toast.error('Failed to navigate');
    }
  };

  const handleEndSession = async () => {
    if (!confirm('Are you sure you want to end this session? All students will be disconnected.')) return;

    try {
      if (sessionManager) {
        await sessionManager.endSession();
      }

      if (sessionId) {
        await updateSession(sessionId, { status: 'ended', ended_at: new Date().toISOString() });
        try {
          await syncSessionAnalytics(sessionId);
        } catch (syncError) {
          console.warn('Analytics sync failed:', syncError);
        }
      }

      if (sessionManager) {
        await sessionManager.cleanup();
      }

      toast.success('Session ended successfully!');
      navigate('/teacher/dashboard');
    } catch (error) {
      console.error('Error ending session:', error);
      toast.error('Failed to end session');
    }
  };

  const handleCopyCode = () => {
    if (!session?.session_code) return;
    navigator.clipboard.writeText(session.session_code);
    setCodeCopied(true);
    toast.success('Session code copied to clipboard!');
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const getLeaderboard = () => {
    return [...participants]
      .sort((a, b) => b.progress_percentage - a.progress_percentage)
      .slice(0, 5)
      .map((p, index) => ({
        name: p.student_name,
        score: p.progress_percentage,
        avatar: ['bg-indigo-500', 'bg-emerald-500', 'bg-violet-500', 'bg-amber-500', 'bg-rose-500'][index % 5]
      }));
  };

  const getEngagementLevel = () => {
    if (stats.totalParticipants === 0) return { level: 'waiting', label: 'Waiting for students...', color: 'text-muted-foreground', bg: 'bg-muted', percentage: 0 };
    const rate = stats.completionRate;
    if (rate >= 80) return { level: 'amazing', label: 'Amazing participation!', color: 'text-emerald-600', bg: 'bg-emerald-500', percentage: rate };
    if (rate >= 60) return { level: 'great', label: 'Great energy!', color: 'text-blue-600', bg: 'bg-blue-500', percentage: rate };
    if (rate >= 40) return { level: 'good', label: 'Keep it going!', color: 'text-amber-600', bg: 'bg-amber-500', percentage: rate };
    if (rate >= 20) return { level: 'warming', label: 'Warming up...', color: 'text-orange-600', bg: 'bg-orange-500', percentage: rate };
    return { level: 'starting', label: 'Just getting started', color: 'text-gray-500', bg: 'bg-gray-400', percentage: rate };
  };

  const getSlideTypeInfo = (slide: Slide) => {
    const config = getSlideConfig(slide.type);
    const isContent = isContentSlideType(slide.type);
    return {
      icon: config?.icon,
      label: config?.label || slide.type,
      isContent,
      color: isContent ? 'bg-sky-100 text-sky-700 border-sky-200' : 'bg-violet-100 text-violet-700 border-violet-200',
      dotColor: isContent ? 'bg-sky-400' : 'bg-violet-400'
    };
  };

  const getMedalIcon = (index: number) => {
    if (index === 0) return <span className="text-lg">🥇</span>;
    if (index === 1) return <span className="text-lg">🥈</span>;
    if (index === 2) return <span className="text-lg">🥉</span>;
    return <span className="text-sm font-bold text-muted-foreground">#{index + 1}</span>;
  };

  const getTimeSince = (date: Date) => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 10) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-violet-50">
        <div className="text-center">
          <div className="relative inline-block mb-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center animate-pulse">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-400 animate-ping" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Setting up your live session...</h2>
          <p className="text-muted-foreground">Preparing the classroom experience</p>
        </div>
      </div>
    );
  }

  if (!session || !slides.length) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-violet-50">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Session not found</h2>
          <p className="text-muted-foreground mb-6">This session may have been ended or doesn't exist.</p>
          <Button onClick={() => navigate('/teacher/dashboard')} size="lg" className="rounded-xl">
            <ArrowRight className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const currentSlideData = slides[currentSlide];
  const leaderboard = getLeaderboard();
  const engagement = getEngagementLevel();
  const currentSlideInfo = getSlideTypeInfo(currentSlideData);

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-w-0">
        
        {/* Enhanced Header with Gradient */}
        <header className="bg-gradient-to-r from-indigo-600 via-indigo-500 to-violet-500 text-white px-6 py-3 shadow-lg relative overflow-hidden">
          {/* Decorative background shapes */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-1/3 w-32 h-32 bg-white rounded-full translate-y-1/2" />
          </div>
          
          <div className="relative flex items-center justify-between gap-4">
            {/* Left: Session Info */}
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-lg font-bold truncate">
                    {(session as any).lessons?.title || 'Live Session'}
                  </h1>
                  {/* Animated LIVE Badge */}
                  <span className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border border-white/30">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                    </span>
                    Live
                  </span>
                </div>
                <p className="text-indigo-100 text-sm">
                  Slide {currentSlide + 1} of {slides.length} &middot; {currentSlideInfo.label}
                </p>
              </div>
            </div>

            {/* Center: Session Code - Prominent */}
            <div className="flex-shrink-0">
              <button
                onClick={handleCopyCode}
                className="group flex items-center gap-3 bg-white/15 hover:bg-white/25 backdrop-blur-sm border-2 border-white/30 hover:border-white/50 rounded-2xl px-5 py-2.5 transition-all duration-200"
                title="Click to copy session code"
              >
                <div className="text-left">
                  <p className="text-[10px] uppercase tracking-wider text-indigo-200 font-semibold leading-none mb-1">Session Code</p>
                  <p className="font-mono text-2xl font-black tracking-[0.2em] leading-none">{session.session_code}</p>
                </div>
                <div className="w-9 h-9 rounded-xl bg-white/20 group-hover:bg-white/30 flex items-center justify-center transition-colors">
                  {codeCopied ? (
                    <Check className="w-4 h-4 text-emerald-300" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </div>
              </button>
            </div>

            {/* Right: Students Count + End Session */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2.5 border border-white/20">
                <Users className="w-5 h-5" />
                <span className="font-bold text-xl tabular-nums">{stats.totalParticipants}</span>
                <span className="text-indigo-200 text-sm hidden lg:inline">students</span>
              </div>
              <Button 
                variant="destructive" 
                onClick={handleEndSession}
                className="rounded-xl bg-red-500/80 hover:bg-red-500 border border-red-400/50 shadow-lg"
                size="sm"
              >
                <X className="w-4 h-4 mr-1.5" />
                End
              </Button>
            </div>
          </div>
        </header>

        {/* Slide Display Area */}
        <div className="flex-1 overflow-auto p-6 bg-gradient-to-b from-indigo-50/40 to-background">
          <div className="max-w-5xl mx-auto">
            
            {/* Current Slide Type Indicator */}
            <div className="flex items-center justify-center gap-3 mb-4">
              <Badge variant="outline" className={`${currentSlideInfo.color} border px-3 py-1 text-xs font-semibold rounded-lg`}>
                {currentSlideInfo.icon && <currentSlideInfo.icon className="w-3.5 h-3.5 mr-1.5" />}
                {currentSlideInfo.label}
              </Badge>
              {!isContentSlideType(currentSlideData.type) && stats.totalParticipants > 0 && (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 px-3 py-1 text-xs font-semibold rounded-lg">
                  <Activity className="w-3.5 h-3.5 mr-1.5" />
                  {getResponseCount(currentSlideSubmissions, stats.totalParticipants)} / {stats.totalParticipants} responded
                </Badge>
              )}
            </div>

            {/* Slide Content Card */}
            <div className="bg-card rounded-2xl border-2 border-border shadow-xl p-8 mb-6 relative overflow-hidden">
              {/* Gradient accent line at top */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-indigo-500" />
              
              <TeacherSlideView
                slide={currentSlideData}
                submissions={currentSlideSubmissions}
                totalParticipants={stats.totalParticipants}
              />
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center justify-between gap-4">
              <Button
                onClick={handlePrevious}
                disabled={currentSlide === 0}
                size="lg"
                variant="outline"
                className="rounded-xl min-w-[140px] h-12 text-base font-semibold border-2 hover:bg-indigo-50 hover:border-indigo-200 disabled:opacity-40"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Previous
              </Button>

              <div className="flex items-center gap-3 flex-1 max-w-md">
                <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                  {currentSlide + 1} / {slides.length}
                </span>
                <Progress 
                  value={((currentSlide + 1) / slides.length) * 100} 
                  className="flex-1 h-2.5"
                />
                <span className="text-xs text-muted-foreground whitespace-nowrap hidden md:inline">
                  ← → keys
                </span>
              </div>

              <Button
                onClick={handleNext}
                disabled={currentSlide >= slides.length - 1}
                size="lg"
                className="rounded-xl min-w-[140px] h-12 text-base font-semibold bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 disabled:opacity-40 shadow-md"
              >
                Next
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Right Sidebar - Enhanced Session Dashboard */}
      <aside className="w-[360px] bg-card border-l border-border overflow-auto flex-shrink-0">
        <div className="p-5">
          
          {/* Class Engagement Meter */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Flame className="w-4 h-4 text-white" />
              </div>
              <h3 className="font-bold text-sm">Class Energy</h3>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/60 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-semibold ${engagement.color}`}>
                  {engagement.label}
                </span>
                <span className="text-lg font-bold tabular-nums">
                  {Math.round(engagement.percentage)}%
                </span>
              </div>
              <div className="w-full h-3 bg-white/80 rounded-full overflow-hidden border border-amber-200/50">
                <div 
                  className={`h-full rounded-full transition-all duration-1000 ease-out ${engagement.bg}`}
                  style={{ width: `${Math.min(engagement.percentage, 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-[10px] text-amber-600/60 mt-1 px-0.5">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100/50 rounded-xl p-3.5 border border-indigo-200/40">
              <div className="flex items-center gap-1.5 mb-1">
                <Activity className="w-3.5 h-3.5 text-indigo-500" />
                <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-500">Submissions</p>
              </div>
              <p className="text-2xl font-bold text-indigo-700 tabular-nums">{stats.totalSubmissions}</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl p-3.5 border border-emerald-200/40">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-500">Avg Progress</p>
              </div>
              <p className="text-2xl font-bold text-emerald-700 tabular-nums">{Math.round(stats.averageProgress)}%</p>
            </div>
          </div>

          {/* Leaderboard */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center">
                <Trophy className="w-4 h-4 text-white" />
              </div>
              <h3 className="font-bold text-sm">Top Performers</h3>
            </div>
            
            {leaderboard.length > 0 ? (
              <div className="space-y-2">
                {leaderboard.map((student, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-3 p-2.5 rounded-xl border transition-all ${
                      index === 0 
                        ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200/60 shadow-sm' 
                        : 'bg-background border-border hover:border-indigo-200'
                    }`}
                  >
                    <div className="w-8 flex items-center justify-center flex-shrink-0">
                      {getMedalIcon(index)}
                    </div>
                    <div className={`w-8 h-8 rounded-full ${student.avatar} flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm`}>
                      {student.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{student.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-indigo-400 to-violet-400 rounded-full transition-all duration-500"
                            style={{ width: `${student.score}%` }}
                          />
                        </div>
                        <span className="text-xs font-bold text-muted-foreground tabular-nums">{student.score}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 bg-muted/30 rounded-xl border border-dashed border-border">
                <Sparkles className="w-8 h-8 mx-auto mb-2 text-amber-400 opacity-60" />
                <p className="text-sm text-muted-foreground">Scores will appear as students participate</p>
              </div>
            )}
          </div>

          {/* Activity Feed */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center">
                <Activity className="w-4 h-4 text-white" />
              </div>
              <h3 className="font-bold text-sm">Live Activity</h3>
            </div>
            
            {activityFeed.length > 0 ? (
              <div className="space-y-1.5 max-h-48 overflow-auto">
                {activityFeed.slice(0, 8).map((item, index) => (
                  <div
                    key={item.id}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all ${
                      index === 0 ? 'bg-indigo-50 border border-indigo-100' : 'bg-muted/30'
                    }`}
                    style={{ 
                      opacity: Math.max(0.4, 1 - index * 0.1),
                      animation: index === 0 ? 'fadeIn 0.3s ease-out' : undefined
                    }}
                  >
                    {item.type === 'join' ? (
                      <>
                        <span className="text-base flex-shrink-0">👋</span>
                        <span className="truncate">
                          <span className="font-semibold">{item.studentName}</span>
                          <span className="text-muted-foreground"> joined</span>
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-base flex-shrink-0">✅</span>
                        <span className="truncate">
                          <span className="font-semibold">{item.studentName}</span>
                          <span className="text-muted-foreground"> answered</span>
                        </span>
                      </>
                    )}
                    <span className="text-[10px] text-muted-foreground ml-auto flex-shrink-0">
                      {getTimeSince(item.timestamp)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-5 bg-muted/30 rounded-xl border border-dashed border-border">
                <Hand className="w-7 h-7 mx-auto mb-2 text-sky-400 opacity-60" />
                <p className="text-xs text-muted-foreground">Student activity will show here</p>
              </div>
            )}
          </div>

          {/* Slide Navigator */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <h3 className="font-bold text-sm">Slide Navigator</h3>
            </div>
            
            {/* Legend */}
            <div className="flex items-center gap-4 mb-2.5 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-sky-400" />
                Content
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-violet-400" />
                Activity
              </span>
            </div>

            <div className="grid grid-cols-5 gap-1.5 max-h-56 overflow-auto pr-1">
              {slides.map((slide, index) => {
                const info = getSlideTypeInfo(slide);
                const isCurrent = currentSlide === index;
                const Icon = info.icon;
                
                return (
                  <button
                    key={slide.id}
                    onClick={() => handleGoToSlide(index)}
                    title={`Slide ${index + 1}: ${info.label}`}
                    className={`relative aspect-square rounded-xl border-2 flex flex-col items-center justify-center gap-0.5 text-xs font-semibold transition-all duration-200 ${
                      isCurrent
                        ? 'border-indigo-500 bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-md shadow-indigo-200 scale-105'
                        : `border-border bg-background hover:border-indigo-300 hover:shadow-sm ${info.isContent ? 'hover:bg-sky-50' : 'hover:bg-violet-50'}`
                    }`}
                  >
                    {Icon && (
                      <Icon className={`w-3 h-3 ${isCurrent ? 'text-white/90' : info.isContent ? 'text-sky-500' : 'text-violet-500'}`} />
                    )}
                    <span className={isCurrent ? 'text-white' : 'text-foreground'}>{index + 1}</span>
                    
                    {/* Type dot indicator */}
                    <div className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${isCurrent ? 'bg-emerald-400' : info.dotColor} border border-white`} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </aside>

      {/* Global animation styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}